import React from 'react';
import { Calendar } from 'lucide-react';

interface YearOption {
  value: string;
  label: string;
  apiValue: number | null; // null means all years
}

interface YearDropdownProps {
  value: number | null;
  onChange: (value: number | null) => void;
  className?: string;
  showIcon?: boolean;
}

// Generate year options dynamically
const generateYearOptions = (): YearOption[] => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-indexed
  
  // Financial year starts in April (month index 3)
  // If we're in Jan-Mar, we're in the FY that started last year
  // If we're in Apr-Dec, we're in the FY that started this year
  const currentFYStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;
  
  const options: YearOption[] = [
    { value: 'all', label: 'All Year Data', apiValue: null },
  ];
  
  // Add years from current FY down to 2023-24
  for (let year = currentFYStartYear; year >= 2023; year--) {
    options.push({
      value: year.toString(),
      label: `${year}-${(year + 1).toString().slice(-2)}`,
      apiValue: year,
    });
  }
  
  return options;
};

export const YEAR_OPTIONS = generateYearOptions();

const YearDropdown: React.FC<YearDropdownProps> = ({
  value,
  onChange,
  className = '',
  showIcon = false,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'all') {
      onChange(null);
    } else {
      onChange(parseInt(selectedValue));
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showIcon && <Calendar className="w-4 h-4 text-gray-500" />}
      <select
        value={value === null ? 'all' : value.toString()}
        onChange={handleChange}
        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer hover:bg-gray-50"
        aria-label="Select Year"
      >
        {YEAR_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default YearDropdown;
